import { ConnectParams } from './MediaStreamConnector';
import { StatusObservableConnector, ConnectionStatusListener } from './StatusObservableConnector';
import * as Sora from '../sora';
import { createListenerManager } from 'infrastructure/utility/ListenerManager';

type Metadata = object;

type EstablishedConnection = {
    connection: Sora.SubscriberConnection<Metadata>;
    stream: MediaStream;
}

function createSoraSubscriberConnection(config: Sora.Settings<Metadata>, roomName: string) {
    return new Promise<EstablishedConnection>((resolve, reject) => {
        try {
            const connection = new Sora.SubscriberConnection<Metadata>(config, (stream: MediaStream) => {
                resolve({ connection, stream });
            });
            connection.start(roomName);
        } catch (e) {
            reject(e);
        }
    });
}

export type SoraConnectionParams = {
    signalingServer: string;
    roomName: string;
    token?: string;
    expire?: number;
}

export class SoraConnector implements StatusObservableConnector {
    private readonly params: SoraConnectionParams;
    private connection: Sora.SubscriberConnection<Metadata> | undefined;
    private activeConnection = false;

    constructor(params: SoraConnectionParams) {
        this.params = params;
    }

    async connect(params: ConnectParams) {
        const { signalingServer, roomName, token, expire } = this.params;
        const { audio } = params;

        const config = {
            signalingServer,
            audio,
            getMetadata: (callback: (metadata: object) => void) => callback({ token, expire }),
        };

        this.activeConnection = true;
        const { connection, stream } = await createSoraSubscriberConnection(config, roomName);
        if (this.activeConnection) {
            this.connection = connection;

            connection.onNotify = notify => {
                this.listenerManager.listeners.forEach(listener => {
                    listener({
                        publishing: notify.channel_upstream_connections > 0,
                    });
                });
            };
        } else {
            // TODO: ここでthrowすべき？
        }

        return { stream, lookupReceiver: () => undefined };
    }

    async disconnect() {
        this.activeConnection = false;

        if (this.connection) {
            this.connection.onNotify = undefined;
            this.connection.stop();
            this.connection = undefined;
        }
    }

    private readonly listenerManager = createListenerManager<ConnectionStatusListener>();

    addStatusListener(listener: ConnectionStatusListener) {
        this.listenerManager.addListener(listener);
    }

    removeStatusListener(listener: ConnectionStatusListener) {
        this.listenerManager.removeListener(listener);
    }
}
